import { parseISO } from 'date-fns'
import type { Moment } from 'moment'
import moment from 'moment'
import type { FC } from 'react'
import { useState, useEffect } from 'react'

import TwoColumnContainer from '@/components/ListWithFiltersContainer'
import PaginatedList from '@/components/PaginatedList'
import SubjectCard from '@/components/SubjectCard'
import Tabs from '@/components/UI/Tabs'
import VisitorPanel from '@/components/VisitorPanel'
import useSavedQueryParam from '@/hooks/useSavedQueryParam'
import useTab from '@/hooks/useTab'
import useUser from '@/hooks/useUser'
import { useGetAllSubjectsQuery, useGetJourneyProgressSubjectsQuery } from '@/lib/apollo'
import type { Subject as SubjectT } from '@/lib/apollo/query/get-all-subjects'
import logger from '@/lib/vouchLogger'

import type { SearchValues as JourneyProgressSearchValues } from './JourneyProgressSearchPanel'
import JourneyProgressSearchPanel, {
  DEFAULT_SEARCH as DEFAULT_JOURNEY_PROGRESS_SEARCH,
} from './JourneyProgressSearchPanel'
import type { SearchValues as OutcomeReviewSearchValues } from './OutcomeReviewSearchPanel'
import OutcomeReviewSearchPanel, {
  DEFAULT_SEARCH as DEFAULT_OUTCOME_REVIEW_SEARCH,
} from './OutcomeReviewSearchPanel'
import { toOutcomeReviewQueryVars, toJourneyProgressQueryVars } from './utils'

type Props = {
  onApplicantSelected: (subjectId: string) => void
  onApplicantAuxSelected: (subjectId: string) => void
}

interface OutcomeReviewSearchState {
  limit: number
  offset: number
  searchValues: OutcomeReviewSearchValues
}

interface JourneyProgressSearchState {
  limit: number
  offset: number
  searchValues: JourneyProgressSearchValues
}

const DEFAULT_OUTCOME_REVIEW_SEARCH_STATE: OutcomeReviewSearchState = {
  limit: 25,
  offset: 0,
  searchValues: DEFAULT_OUTCOME_REVIEW_SEARCH,
}

const DEFAULT_JOURNEY_PROGRESS_SEARCH_STATE: JourneyProgressSearchState = {
  limit: 25,
  offset: 0,
  searchValues: DEFAULT_JOURNEY_PROGRESS_SEARCH,
}

// Each population's search state is persisted independently, so switching
// tabs never needs to migrate or merge shapes between them.
const OUTCOME_REVIEW_SEARCH_KEY = 'searchV28OutcomeReview'
const JOURNEY_PROGRESS_SEARCH_KEY = 'searchV28JourneyProgress'

const POPULATIONS = ['outcomeReview', 'journeyProgress'] as const
type Population = (typeof POPULATIONS)[number]

const Subjects: FC<Props> = ({ onApplicantSelected, onApplicantAuxSelected }) => {
  const { customerId, personId } = useUser()

  const { selectedTab: population, handleChangeTab: onChangePopulation } =
    useTab<Population>({
      availableTabs: POPULATIONS,
      defaultTab: 'outcomeReview',
    })

  const {
    queryParamValue: committedOutcomeReviewSearch,
    onSaveQueryParamValue: setCommittedOutcomeReviewSearch,
  } = useSavedQueryParam({
    storageKey: OUTCOME_REVIEW_SEARCH_KEY,
    defaultState: DEFAULT_OUTCOME_REVIEW_SEARCH_STATE,
  })

  const {
    queryParamValue: committedJourneyProgressSearch,
    onSaveQueryParamValue: setCommittedJourneyProgressSearch,
  } = useSavedQueryParam({
    storageKey: JOURNEY_PROGRESS_SEARCH_KEY,
    defaultState: DEFAULT_JOURNEY_PROGRESS_SEARCH_STATE,
  })

  const [draftOutcomeReviewSearch, setDraftOutcomeReviewSearch] =
    useState<OutcomeReviewSearchState>(committedOutcomeReviewSearch)
  const [draftJourneyProgressSearch, setDraftJourneyProgressSearch] =
    useState<JourneyProgressSearchState>(committedJourneyProgressSearch)

  const [lastUpdated, setLastUpdated] = useState<Moment>(moment())

  const logFilteredSearch = (filterTab: Population, searchFilters: object) => {
    logger.info('Searched applicants filters', {
      personId,
      customerId,
      filterTab,
      searchFilters,
    })
  }

  const [fetchOutcomeReview, outcomeReviewResult] = useGetAllSubjectsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    onCompleted: data => {
      setLastUpdated(moment())

      if (
        data.getSubjects.results.length === 0 &&
        committedOutcomeReviewSearch.offset !== 0
      ) {
        onChangeOutcomeReviewPage(committedOutcomeReviewSearch.limit, 0)
      }
    },
    onError: () => {
      setLastUpdated(moment())
    },
  })

  const [fetchJourneyProgress, journeyProgressResult] =
    useGetJourneyProgressSubjectsQuery({
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      onCompleted: data => {
        setLastUpdated(moment())

        if (
          data.getJourneyProgressSubjects.results.length === 0 &&
          committedJourneyProgressSearch.offset !== 0
        ) {
          onChangeJourneyProgressPage(committedJourneyProgressSearch.limit, 0)
        }
      },
      onError: () => {
        setLastUpdated(moment())
      },
    })

  const onCommitOutcomeReviewSearchState = (
    newCommittedSearch: OutcomeReviewSearchState,
  ) => {
    setCommittedOutcomeReviewSearch(newCommittedSearch)
    fetchOutcomeReview({
      variables: toOutcomeReviewQueryVars({
        customerId,
        ...newCommittedSearch,
      }),
    })
  }

  const onCommitJourneyProgressSearchState = (
    newCommittedSearch: JourneyProgressSearchState,
  ) => {
    setCommittedJourneyProgressSearch(newCommittedSearch)
    fetchJourneyProgress({
      variables: toJourneyProgressQueryVars({
        customerId,
        ...newCommittedSearch,
      }),
    })
  }

  const onChangeOutcomeReviewPage = (limit: number, offset: number) => {
    const newCommittedSearch = {
      ...committedOutcomeReviewSearch,
      offset,
      limit,
    }

    setDraftOutcomeReviewSearch(newCommittedSearch)
    onCommitOutcomeReviewSearchState(newCommittedSearch)
  }

  const onChangeJourneyProgressPage = (limit: number, offset: number) => {
    const newCommittedSearch = {
      ...committedJourneyProgressSearch,
      offset,
      limit,
    }

    setDraftJourneyProgressSearch(newCommittedSearch)
    onCommitJourneyProgressSearchState(newCommittedSearch)
  }

  useEffect(() => {
    if (population === 'journeyProgress') {
      logFilteredSearch(
        'journeyProgress',
        committedJourneyProgressSearch.searchValues.filter,
      )
      fetchJourneyProgress({
        variables: toJourneyProgressQueryVars({
          customerId,
          ...committedJourneyProgressSearch,
        }),
      })
      return
    }

    logFilteredSearch('outcomeReview', committedOutcomeReviewSearch.searchValues.filter)
    fetchOutcomeReview({
      variables: toOutcomeReviewQueryVars({
        customerId,
        ...committedOutcomeReviewSearch,
      }),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [population, fetchOutcomeReview, fetchJourneyProgress])

  const isJourneyProgress = population === 'journeyProgress'

  const { loading, error, results, total } = isJourneyProgress
    ? {
        loading: journeyProgressResult.loading || !journeyProgressResult.called,
        error: journeyProgressResult.error,
        results: journeyProgressResult.data?.getJourneyProgressSubjects?.results ?? [],
        total: journeyProgressResult.data?.getJourneyProgressSubjects?.total ?? 0,
      }
    : {
        loading: outcomeReviewResult.loading || !outcomeReviewResult.called,
        error: outcomeReviewResult.error,
        results: outcomeReviewResult.data?.getSubjects?.results ?? [],
        total: outcomeReviewResult.data?.getSubjects?.total ?? 0,
      }

  const { limit, offset } = isJourneyProgress
    ? committedJourneyProgressSearch
    : committedOutcomeReviewSearch

  const onChangePage = isJourneyProgress
    ? onChangeJourneyProgressPage
    : onChangeOutcomeReviewPage

  return (
    <TwoColumnContainer>
      <div>
        <Tabs
          value={population}
          onChange={onChangePopulation}
          config={[
            { label: 'Outcome review', value: 'outcomeReview' },
            { label: 'Journey progress', value: 'journeyProgress' },
          ]}
        />
        {isJourneyProgress ? (
          <JourneyProgressSearchPanel
            searchValues={draftJourneyProgressSearch.searchValues}
            onChangeSearchValues={searchValues =>
              setDraftJourneyProgressSearch({
                ...draftJourneyProgressSearch,
                searchValues,
              })
            }
            onSubmitSearchValues={newSearchValues => {
              const newCommittedSearch = {
                ...committedJourneyProgressSearch,
                offset: DEFAULT_JOURNEY_PROGRESS_SEARCH_STATE.offset,
                searchValues: newSearchValues,
              }

              logFilteredSearch('journeyProgress', newSearchValues.filter)
              onCommitJourneyProgressSearchState(newCommittedSearch)
            }}
          />
        ) : (
          <OutcomeReviewSearchPanel
            searchValues={draftOutcomeReviewSearch.searchValues}
            onChangeSearchValues={searchValues =>
              setDraftOutcomeReviewSearch({
                ...draftOutcomeReviewSearch,
                searchValues,
              })
            }
            onSubmitSearchValues={newSearchValues => {
              const newCommittedSearch = {
                ...committedOutcomeReviewSearch,
                offset: DEFAULT_OUTCOME_REVIEW_SEARCH_STATE.offset,
                searchValues: newSearchValues,
              }

              logFilteredSearch('outcomeReview', newSearchValues.filter)
              onCommitOutcomeReviewSearchState(newCommittedSearch)
            }}
          />
        )}
      </div>
      <PaginatedList<SubjectT>
        loading={loading}
        lastUpdated={lastUpdated}
        total={total}
        error={error}
        limit={limit}
        offset={offset}
        onChangePage={onChangePage}
        data={results}
        row={subject => (
          <SubjectCard
            avatarUrl={subject.avatar}
            onClick={() => onApplicantSelected(subject.subjectId)}
            onAuxClick={() => onApplicantAuxSelected(subject.subjectId)}
            customerClientName={subject.customerClientName}
            name={subject.name}
            email={subject.email ?? ''}
            type={subject.type ?? 'tenant'}
            address={subject.address}
            milestones={subject.milestones}
            tenancyStartDate={parseISO(subject.tenancyStartDate)}
            visitorPanel={<VisitorPanel visitors={subject.visitors} />}
            referenceTier={subject.referenceTier}
          />
        )}
      />
    </TwoColumnContainer>
  )
}

export default Subjects
